import type { TourCompletionRecord } from './types'

const STORAGE_PREFIX = 'paintscout.tour'

/** The localStorage key a tour's completion record is written to. */
export function getTourStorageKey(tourId: string): string {
  return `${STORAGE_PREFIX}.${tourId}`
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && !!window.localStorage
}

/**
 * True when an automated end-to-end run is driving the browser.
 *
 * The BugBug suite sets this key from its shared login component. `apps/shared/util/e2e.ts` owns
 * it, but this package cannot import from `apps/shared`, so the key is repeated here. Change both
 * together.
 */
const E2E_STORAGE_KEY = 'ps-e2e'

export function isAutomatedRun(): boolean {
  if (!isBrowser()) return false

  try {
    return window.localStorage.getItem(E2E_STORAGE_KEY) === '1'
  } catch {
    // localStorage can throw in private mode. An unreadable key is not an automated run.
    return false
  }
}

export function readTourRecord(tourId: string): TourCompletionRecord | null {
  if (!isBrowser()) return null

  try {
    const raw = window.localStorage.getItem(getTourStorageKey(tourId))
    if (!raw) return null

    const parsed = JSON.parse(raw) as TourCompletionRecord
    // Anything that isn't a record we wrote is treated as "not seen" rather than trusted.
    if (!parsed || typeof parsed !== 'object' || typeof parsed.version !== 'number') return null
    return parsed
  } catch (error) {
    console.warn(`Tour: error reading completion record for "${tourId}"`, error)
    return null
  }
}

/**
 * Whether this browser has already finished (or dismissed) the tour at `version` or newer.
 * Bumping a tour's `version` invalidates old records, so a reworked tour shows again.
 */
export function hasSeenTour(tourId: string, version = 1): boolean {
  const record = readTourRecord(tourId)
  return !!record && record.version >= version
}

export function markTourSeen(tourId: string, { version = 1, dismissed = false } = {}): void {
  if (!isBrowser()) return

  const record: TourCompletionRecord = {
    completedAt: new Date().toISOString(),
    version,
    ...(dismissed ? { dismissed: true } : {})
  }

  try {
    window.localStorage.setItem(getTourStorageKey(tourId), JSON.stringify(record))
  } catch (error) {
    console.warn(`Tour: error writing completion record for "${tourId}"`, error)
  }
}

/** Clears the record so the tour auto-starts again — handy for a "replay the tour" affordance. */
export function resetTour(tourId: string): void {
  if (!isBrowser()) return

  try {
    window.localStorage.removeItem(getTourStorageKey(tourId))
  } catch (error) {
    console.warn(`Tour: error clearing completion record for "${tourId}"`, error)
  }
}
